import createPropertyRecord from '../../helpers/createPropertyRecord';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

const createPropertyFunction = (id, name, value) => {
  return {
    id,
    name,
    value,
    readonly: false,
    regular: false,
  };
};

const useSplitedProperties = (id) => {
  const propertiesObject = useSelector((state) => state.presets[id]);

  const type = useMemo(() => {
    return [
      createPropertyRecord(`presets.${id}.useAsPencil`, 'useAsPencil', propertiesObject.useAsPencil, 'boolean'),
      createPropertyRecord(`presets.${id}.useAsBrush`, 'useAsBrush', propertiesObject.useAsBrush, 'boolean'),
      createPropertyRecord(`presets.${id}.useAsRag`, 'useAsRag', propertiesObject.useAsRag, 'boolean'),
    ];
  }, [propertiesObject, id]);

  const shape = useMemo(() => {
    return [
      createPropertyRecord(`presets.${id}.bristleCount`, 'bristleCount', propertiesObject.bristleCount, 'integer'),
      createPropertyRecord(`presets.${id}.verticesPerBristle`, 'verticesPerBristle', propertiesObject.verticesPerBristle, 'integer'),
      createPropertyRecord(`presets.${id}.bristleJitter`, 'bristleJitter', propertiesObject.bristleJitter, 'float'),
      createPropertyRecord(`presets.${id}.bristleLength`, 'bristleLength', propertiesObject.bristleLength, 'float'),
      createPropertyRecord(`presets.${id}.brushHeight`, 'brushHeight', propertiesObject.brushHeight, 'float'),
      createPropertyRecord(`presets.${id}.brushScale`, 'brushScale', propertiesObject.brushScale, 'float'),
      createPropertyRecord(`presets.${id}.normalScale`, 'normalScale', propertiesObject.normalScale, 'float'),
      createPropertyRecord(`presets.${id}.splatRadius`, 'splatRadius', propertiesObject.splatRadius, 'float'),
    ];
  }, [propertiesObject, id]);

  const physics = useMemo(() => {
    return [
      createPropertyRecord(`presets.${id}.brushDamping`, 'brushDamping', propertiesObject.brushDamping, 'float'),
      createPropertyRecord(`presets.${id}.brushGravity`, 'brushGravity', propertiesObject.brushGravity, 'float'),
      createPropertyRecord(`presets.${id}.brushZThreshold`, 'brushZThreshold', propertiesObject.brushZThreshold, 'float'),
      createPropertyRecord(`presets.${id}.stiffnessVariation`, 'stiffnessVariation', propertiesObject.stiffnessVariation, 'float'),
      createPropertyRecord(`presets.${id}.brushIterations`, 'brushIterations', propertiesObject.brushIterations, 'integer'),
      createPropertyRecord(`presets.${id}.splatsPerSegment`, 'splatsPerSegment', propertiesObject.splatsPerSegment, 'integer'),
    ];
  }, [propertiesObject, id]);

  const simulation = useMemo(() => {
    return [
      createPropertyRecord(`presets.${id}.fluidityIsOn`, 'fluidityIsOn', propertiesObject.fluidityIsOn, 'boolean'),
      createPropertyRecord(`presets.${id}.framesToSimulate`, 'framesToSimulate', propertiesObject.framesToSimulate, 'integer'),
      createPropertyRecord(`presets.${id}.pressureJacobiIterations`, 'pressureJacobiIterations', propertiesObject.pressureJacobiIterations, 'integer'),
      createPropertyRecord(`presets.${id}.nPreviousSpeeds`, 'nPreviousSpeeds', propertiesObject.nPreviousSpeeds, 'integer'),
      createPropertyRecord(`presets.${id}.splatVelocityScale`, 'splatVelocityScale', propertiesObject.splatVelocityScale, 'float'),
    ];
  }, [propertiesObject, id]);

  const primaryBehavior = useMemo(() => {
    return [
      createPropertyRecord(`presets.${id}.primaryDeltaTimeDivider`, 'primaryDeltaTimeDivider', propertiesObject.primaryDeltaTimeDivider, 'integer'),

      // min alpha
      createPropertyRecord(`presets.${id}.primaryMinAlphaFrom`, 'primaryMinAlphaFrom', propertiesObject.primaryMinAlphaFrom, 'float'),
      createPropertyRecord(`presets.${id}.primaryMinAlphaTo`, 'primaryMinAlphaTo', propertiesObject.primaryMinAlphaTo, 'float'),
      createPropertyFunction(`presets.${id}.primaryMinAlphaFunction`, 'primaryMinAlphaFunction', propertiesObject.primaryMinAlphaFunction),
      createPropertyRecord(`presets.${id}.primaryMinAlphaAsMedium`, 'primaryMinAlphaAsMedium', propertiesObject.primaryMinAlphaAsMedium, 'boolean'),
      createPropertyRecord(`presets.${id}.primaryMinAlphaAsForce`, 'primaryMinAlphaAsForce', propertiesObject.primaryMinAlphaAsForce, 'boolean'),
      createPropertyRecord(
        `presets.${id}.primaryMinAlphaConstantScale`,
        'primaryMinAlphaConstantScale',
        propertiesObject.primaryMinAlphaConstantScale,
        'float'
      ),

      // max alpha
      createPropertyRecord(`presets.${id}.primaryMaxAlphaFrom`, 'primaryMaxAlphaFrom', propertiesObject.primaryMaxAlphaFrom, 'float'),
      createPropertyRecord(`presets.${id}.primaryMaxAlphaTo`, 'primaryMaxAlphaTo', propertiesObject.primaryMaxAlphaTo, 'float'),
      createPropertyFunction(`presets.${id}.primaryMaxAlphaFunction`, 'primaryMaxAlphaFunction', propertiesObject.primaryMaxAlphaFunction),
      createPropertyRecord(`presets.${id}.primaryMaxAlphaAsMedium`, 'primaryMaxAlphaAsMedium', propertiesObject.primaryMaxAlphaAsMedium, 'boolean'),
      createPropertyRecord(`presets.${id}.primaryMaxAlphaAsForce`, 'primaryMaxAlphaAsForce', propertiesObject.primaryMaxAlphaAsForce, 'boolean'),
      createPropertyRecord(
        `presets.${id}.primaryMaxAlphaConstantScale`,
        'primaryMaxAlphaConstantScale',
        propertiesObject.primaryMaxAlphaConstantScale,
        'float'
      ),

      // alpha mixing
      createPropertyFunction(`presets.${id}.primaryAlphaFunction`, 'primaryAlphaFunction', propertiesObject.primaryAlphaFunction),
      createPropertyRecord(`presets.${id}.primaryAlphaAsMedium`, 'primaryAlphaAsMedium', propertiesObject.primaryAlphaAsMedium, 'boolean'),
      createPropertyRecord(`presets.${id}.primaryAlphaAsForce`, 'primaryAlphaAsForce', propertiesObject.primaryAlphaAsForce, 'boolean'),
      createPropertyRecord(`presets.${id}.primaryAlphaConstantScale`, 'primaryAlphaConstantScale', propertiesObject.primaryAlphaConstantScale, 'float'),
      createPropertyRecord(
        `presets.${id}.primaryAlphaInvertedArgument`,
        'primaryAlphaInvertedArgument',
        propertiesObject.primaryAlphaInvertedArgument,
        'boolean'
      ),

      // pen force alpha
      createPropertyRecord(`presets.${id}.primaryPenForceAlphaIsOn`, 'primaryPenForceAlphaIsOn', propertiesObject.primaryPenForceAlphaIsOn, 'boolean'),
      createPropertyRecord(`presets.${id}.primaryPenForceAlphaFrom`, 'primaryPenForceAlphaFrom', propertiesObject.primaryPenForceAlphaFrom, 'float'),
      createPropertyRecord(`presets.${id}.primaryPenForceAlphaTo`, 'primaryPenForceAlphaTo', propertiesObject.primaryPenForceAlphaTo, 'float'),
      createPropertyFunction(`presets.${id}.primaryPenForceAlphaFunction`, 'primaryPenForceAlphaFunction', propertiesObject.primaryPenForceAlphaFunction),

      // pen force brush size
      createPropertyRecord(
        `presets.${id}.primaryPenForceBrushSizeIsOn`,
        'primaryPenForceBrushSizeIsOn',
        propertiesObject.primaryPenForceBrushSizeIsOn,
        'boolean'
      ),
      createPropertyRecord(
        `presets.${id}.primaryPenForceBrushSizeFrom`,
        'primaryPenForceBrushSizeFrom',
        propertiesObject.primaryPenForceBrushSizeFrom,
        'float'
      ),
      createPropertyRecord(`presets.${id}.primaryPenForceBrushSizeTo`, 'primaryPenForceBrushSizeTo', propertiesObject.primaryPenForceBrushSizeTo, 'float'),
      createPropertyFunction(
        `presets.${id}.primaryPenForceBrushSizeFunction`,
        'primaryPenForceBrushSizeFunction',
        propertiesObject.primaryPenForceBrushSizeFunction
      ),

      // pen force splat radius size
      createPropertyRecord(
        `presets.${id}.primaryPenForceSplatRadiusSizeIsOn`,
        'primaryPenForceSplatRadiusSizeIsOn',
        propertiesObject.primaryPenForceSplatRadiusSizeIsOn,
        'boolean'
      ),
      createPropertyRecord(
        `presets.${id}.primaryPenForceSplatRadiusSizeFrom`,
        'primaryPenForceSplatRadiusSizeFrom',
        propertiesObject.primaryPenForceSplatRadiusSizeFrom,
        'float'
      ),
      createPropertyRecord(
        `presets.${id}.primaryPenForceSplatRadiusSizeTo`,
        'primaryPenForceSplatRadiusSizeTo',
        propertiesObject.primaryPenForceSplatRadiusSizeTo,
        'float'
      ),
      createPropertyFunction(
        `presets.${id}.primaryPenForceSplatRadiusSizeFunction`,
        'primaryPenForceSplatRadiusSizeFunction',
        propertiesObject.primaryPenForceSplatRadiusSizeFunction
      ),

      // fluidity
      createPropertyRecord(`presets.${id}.primaryFluidityFrom`, 'primaryFluidityFrom', propertiesObject.primaryFluidityFrom, 'float'),
      createPropertyRecord(`presets.${id}.primaryFluidityTo`, 'primaryFluidityTo', propertiesObject.primaryFluidityTo, 'float'),
      createPropertyFunction(`presets.${id}.primaryFluidityFunction`, 'primaryFluidityFunction', propertiesObject.primaryFluidityFunction),
    ];
  }, [propertiesObject, id]);

  const secondaryBehavior = useMemo(() => {
    return [
      createPropertyRecord(`presets.${id}.secondaryDeltaTimeDivider`, 'secondaryDeltaTimeDivider', propertiesObject.secondaryDeltaTimeDivider, 'integer'),

      // min alpha
      createPropertyRecord(`presets.${id}.secondaryMinAlphaFrom`, 'secondaryMinAlphaFrom', propertiesObject.secondaryMinAlphaFrom, 'float'),
      createPropertyRecord(`presets.${id}.secondaryMinAlphaTo`, 'secondaryMinAlphaTo', propertiesObject.secondaryMinAlphaTo, 'float'),
      createPropertyFunction(`presets.${id}.secondaryMinAlphaFunction`, 'secondaryMinAlphaFunction', propertiesObject.secondaryMinAlphaFunction),
      createPropertyRecord(`presets.${id}.secondaryMinAlphaAsMedium`, 'secondaryMinAlphaAsMedium', propertiesObject.secondaryMinAlphaAsMedium, 'boolean'),
      createPropertyRecord(`presets.${id}.secondaryMinAlphaAsForce`, 'secondaryMinAlphaAsForce', propertiesObject.secondaryMinAlphaAsForce, 'boolean'),
      createPropertyRecord(
        `presets.${id}.secondaryMinAlphaConstantScale`,
        'secondaryMinAlphaConstantScale',
        propertiesObject.secondaryMinAlphaConstantScale,
        'float'
      ),

      // max alpha
      createPropertyRecord(`presets.${id}.secondaryMaxAlphaFrom`, 'secondaryMaxAlphaFrom', propertiesObject.secondaryMaxAlphaFrom, 'float'),
      createPropertyRecord(`presets.${id}.secondaryMaxAlphaTo`, 'secondaryMaxAlphaTo', propertiesObject.secondaryMaxAlphaTo, 'float'),
      createPropertyFunction(`presets.${id}.secondaryMaxAlphaFunction`, 'secondaryMaxAlphaFunction', propertiesObject.secondaryMaxAlphaFunction),
      createPropertyRecord(`presets.${id}.secondaryMaxAlphaAsMedium`, 'secondaryMaxAlphaAsMedium', propertiesObject.secondaryMaxAlphaAsMedium, 'boolean'),
      createPropertyRecord(`presets.${id}.secondaryMaxAlphaAsForce`, 'secondaryMaxAlphaAsForce', propertiesObject.secondaryMaxAlphaAsForce, 'boolean'),
      createPropertyRecord(
        `presets.${id}.secondaryMaxAlphaConstantScale`,
        'secondaryMaxAlphaConstantScale',
        propertiesObject.secondaryMaxAlphaConstantScale,
        'float'
      ),

      // alpha mixing
      createPropertyFunction(`presets.${id}.secondaryAlphaFunction`, 'secondaryAlphaFunction', propertiesObject.secondaryAlphaFunction),
      createPropertyRecord(`presets.${id}.secondaryAlphaAsMedium`, 'secondaryAlphaAsMedium', propertiesObject.secondaryAlphaAsMedium, 'boolean'),
      createPropertyRecord(`presets.${id}.secondaryAlphaAsForce`, 'secondaryAlphaAsForce', propertiesObject.secondaryAlphaAsForce, 'boolean'),
      createPropertyRecord(`presets.${id}.secondaryAlphaConstantScale`, 'secondaryAlphaConstantScale', propertiesObject.secondaryAlphaConstantScale, 'float'),
      createPropertyRecord(
        `presets.${id}.secondaryAlphaInvertedArgument`,
        'secondaryAlphaInvertedArgument',
        propertiesObject.secondaryAlphaInvertedArgument,
        'boolean'
      ),

      // pen force alpha
      createPropertyRecord(`presets.${id}.secondaryPenForceAlphaIsOn`, 'secondaryPenForceAlphaIsOn', propertiesObject.secondaryPenForceAlphaIsOn, 'boolean'),
      createPropertyRecord(`presets.${id}.secondaryPenForceAlphaFrom`, 'secondaryPenForceAlphaFrom', propertiesObject.secondaryPenForceAlphaFrom, 'float'),
      createPropertyRecord(`presets.${id}.secondaryPenForceAlphaTo`, 'secondaryPenForceAlphaTo', propertiesObject.secondaryPenForceAlphaTo, 'float'),
      createPropertyFunction(`presets.${id}.secondaryPenForceAlphaFunction`, 'secondaryPenForceAlphaFunction', propertiesObject.secondaryPenForceAlphaFunction),

      // pen force brush size
      createPropertyRecord(
        `presets.${id}.secondaryPenForceBrushSizeIsOn`,
        'secondaryPenForceBrushSizeIsOn',
        propertiesObject.secondaryPenForceBrushSizeIsOn,
        'boolean'
      ),
      createPropertyRecord(
        `presets.${id}.secondaryPenForceBrushSizeFrom`,
        'secondaryPenForceBrushSizeFrom',
        propertiesObject.secondaryPenForceBrushSizeFrom,
        'float'
      ),
      createPropertyRecord(
        `presets.${id}.secondaryPenForceBrushSizeTo`,
        'secondaryPenForceBrushSizeTo',
        propertiesObject.secondaryPenForceBrushSizeTo,
        'float'
      ),
      createPropertyFunction(
        `presets.${id}.secondaryPenForceBrushSizeFunction`,
        'secondaryPenForceBrushSizeFunction',
        propertiesObject.secondaryPenForceBrushSizeFunction
      ),

      // pen force splat radius size
      createPropertyRecord(
        `presets.${id}.secondaryPenForceSplatRadiusSizeIsOn`,
        'secondaryPenForceSplatRadiusSizeIsOn',
        propertiesObject.secondaryPenForceSplatRadiusSizeIsOn,
        'boolean'
      ),
      createPropertyRecord(
        `presets.${id}.secondaryPenForceSplatRadiusSizeFrom`,
        'secondaryPenForceSplatRadiusSizeFrom',
        propertiesObject.secondaryPenForceSplatRadiusSizeFrom,
        'float'
      ),
      createPropertyRecord(
        `presets.${id}.secondaryPenForceSplatRadiusSizeTo`,
        'secondaryPenForceSplatRadiusSizeTo',
        propertiesObject.secondaryPenForceSplatRadiusSizeTo,
        'float'
      ),
      createPropertyFunction(
        `presets.${id}.secondaryPenForceSplatRadiusSizeFunction`,
        'secondaryPenForceSplatRadiusSizeFunction',
        propertiesObject.secondaryPenForceSplatRadiusSizeFunction
      ),

      // fluidity
      createPropertyRecord(`presets.${id}.secondaryFluidityFrom`, 'secondaryFluidityFrom', propertiesObject.secondaryFluidityFrom, 'float'),
      createPropertyRecord(`presets.${id}.secondaryFluidityTo`, 'secondaryFluidityTo', propertiesObject.secondaryFluidityTo, 'float'),
      createPropertyFunction(`presets.${id}.secondaryFluidityFunction`, 'secondaryFluidityFunction', propertiesObject.secondaryFluidityFunction),
    ];
  }, [propertiesObject, id]);

  const paintRemoving = useMemo(() => {
    return [
      createPropertyRecord(`presets.${id}.paintRemovingScale`, 'paintRemovingScale', propertiesObject.paintRemovingScale, 'float'),
      createPropertyRecord(`presets.${id}.paintRemovingMaxSize`, 'paintRemovingMaxSize', propertiesObject.paintRemovingMaxSize, 'float'),
    ];
  }, [propertiesObject, id]);

  const depletion = useMemo(() => {
    return [
      createPropertyRecord(`presets.${id}.depletionIsOn`, 'depletionIsOn', propertiesObject.depletionIsOn, 'boolean'),
      createPropertyRecord(`presets.${id}.depletionMaxSpeed`, 'depletionMaxSpeed', propertiesObject.depletionMaxSpeed, 'float'),
      createPropertyFunction(`presets.${id}.depletionFunction`, 'depletionFunction', propertiesObject.depletionFunction),
    ];
  }, [propertiesObject, id]);

  const limitations = useMemo(() => {
    return [
      createPropertyRecord(`presets.${id}.limitationsIsOn`, 'limitationsIsOn', propertiesObject.limitationsIsOn, 'boolean'),
      createPropertyRecord(`presets.${id}.limitationsMaxLength`, 'limitationsMaxLength', propertiesObject.limitationsMaxLength, 'integer'),
      createPropertyRecord(`presets.${id}.limitationsMaxArea`, 'limitationsMaxArea', propertiesObject.limitationsMaxArea, 'integer'),
      createPropertyRecord(`presets.${id}.limitationsFrom`, 'limitationsFrom', propertiesObject.limitationsFrom, 'float'),
      createPropertyFunction(`presets.${id}.limitationsFunction`, 'limitationsFunction', propertiesObject.limitationsFunction),
    ];
  }, [propertiesObject, id]);

  return {
    type,
    shape,
    physics,
    simulation,
    depletion,
    limitations,
    paintRemoving,
    primaryBehavior,
    secondaryBehavior,
  };
};

export default useSplitedProperties;
